/**
 * Helpers for "disable Save until something actually changed" on edit forms.
 *
 * Edit dialogs compare the payload they *would* submit against the payload the
 * record was loaded with. A plain `JSON.stringify` comparison is fragile because
 * key order is significant — an object rebuilt by a change handler can serialize
 * differently while being logically identical, which would light up the Save
 * button for a no-op. `stableStringify` normalises key order at every depth so
 * only real value changes register.
 */

/** JSON with object keys sorted at every level, so key order can't fake a diff. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.fromEntries(
        Object.entries(val as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
      );
    }
    return val;
  });
}

/**
 * True when the two payloads are value-identical (key order and `undefined`
 * properties ignored, array order still significant).
 */
export function isSamePayload(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}
